import { useState } from 'react';

/**
 * Custom hook for managing all filter states in the statistics dashboard
 */
const useStatisticsFilters = (defaultValues) => {
  // Demand section filter states
  const [demandFilterType, setDemandFilterType] = useState(defaultValues.demandFilterType);
  const [selectedUserTypes, setSelectedUserTypes] = useState(defaultValues.selectedUserTypes);
  const [userCreationPeriod, setUserCreationPeriod] = useState(defaultValues.userCreationPeriod);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [demandYAxisPercentage, setDemandYAxisPercentage] = useState(true); // Default to true for demand

  // Cart filter states for availability section
  const [availabilityCartFilterType, setAvailabilityCartFilterType] = useState(defaultValues.availabilityFilterType);
  const [selectedAvailabilityCartTypes, setSelectedAvailabilityCartTypes] = useState(defaultValues.availabilityCartTypes);
  const [selectedAvailabilityCarts, setSelectedAvailabilityCarts] = useState([]);
  const [availabilityYAxisPercentage, setAvailabilityYAxisPercentage] = useState(true); // Y-axis percentage for availability

  // Cart filter states for onService section
  const [onServiceCartFilterType, setOnServiceCartFilterType] = useState(defaultValues.onServiceFilterType);
  const [selectedOnServiceCartTypes, setSelectedOnServiceCartTypes] = useState(defaultValues.onServiceCartTypes);
  const [selectedOnServiceCarts, setSelectedOnServiceCarts] = useState([]);
  const [onServiceYAxisPercentage, setOnServiceYAxisPercentage] = useState(true); // Y-axis percentage for onService

  // Cart filter states for waitingTime section
  const [waitingTimeCartFilterType, setWaitingTimeCartFilterType] = useState(defaultValues.waitingTimeFilterType);
  const [selectedWaitingTimeCartTypes, setSelectedWaitingTimeCartTypes] = useState(defaultValues.waitingTimeCartTypes);
  const [selectedWaitingTimeCarts, setSelectedWaitingTimeCarts] = useState([]);
  const [waitingTimeYAxisPercentage, setWaitingTimeYAxisPercentage] = useState(true); // Y-axis percentage for waitingTime

  // Trip filter states
  const [tripCartFilterType, setTripCartFilterType] = useState(defaultValues.tripFilterType);
  const [selectedTripCartTypes, setSelectedTripCartTypes] = useState(defaultValues.tripCartTypes);
  const [selectedTripCarts, setSelectedTripCarts] = useState([]);
  const [selectedTripStatuses, setSelectedTripStatuses] = useState(defaultValues.tripStatuses);
  const [tripType, setTripType] = useState(defaultValues.tripType);
  const [tripYAxisPercentage, setTripYAxisPercentage] = useState(true); // Y-axis percentage for trips

  // Cart summary filter state
  const [cartSummaryFilter, setCartSummaryFilter] = useState('all_active');

  // Demand filter handlers
  const handleUserTypesChange = (newUserTypes) => {
    // Prevent deselecting all user types - minimum 1 must be selected
    if (newUserTypes.length === 0) return;
    setSelectedUserTypes(newUserTypes);
  };

  const handleCreationPeriodChange = (newPeriod) => {
    setUserCreationPeriod(newPeriod);
  };

  const handleSpecificUsersChange = (newUsers) => {
    setSelectedUsers(newUsers);
  };

  // Y-axis percentage handlers for all sections
  const handleDemandYAxisPercentageChange = (newValue) => {
    setDemandYAxisPercentage(newValue);
  };
  
  const handleAvailabilityYAxisPercentageChange = (newValue) => {
    setAvailabilityYAxisPercentage(newValue);
  };
  
  const handleOnServiceYAxisPercentageChange = (newValue) => {
    setOnServiceYAxisPercentage(newValue);
  };
  
  const handleWaitingTimeYAxisPercentageChange = (newValue) => {
    setWaitingTimeYAxisPercentage(newValue);
  };
  
  const handleTripYAxisPercentageChange = (newValue) => {
    setTripYAxisPercentage(newValue);
  };

  // Availability cart filter handlers
  const handleAvailabilityCartTypesChange = (newCartTypes) => {
    // Prevent deselecting all cart types - minimum 1 must be selected
    if (newCartTypes.length === 0) return;
    setSelectedAvailabilityCartTypes(newCartTypes);
  };

  const handleAvailabilitySpecificCartsChange = (newCarts) => {
    setSelectedAvailabilityCarts(newCarts);
  };

  // OnService cart filter handlers
  const handleOnServiceCartTypesChange = (newCartTypes) => {
    // Prevent deselecting all cart types - minimum 1 must be selected
    if (newCartTypes.length === 0) return;
    setSelectedOnServiceCartTypes(newCartTypes);
  };

  const handleOnServiceSpecificCartsChange = (newCarts) => {
    setSelectedOnServiceCarts(newCarts);
  };

  // WaitingTime cart filter handlers
  const handleWaitingTimeCartTypesChange = (newCartTypes) => {
    // Prevent deselecting all cart types - minimum 1 must be selected
    if (newCartTypes.length === 0) return;
    setSelectedWaitingTimeCartTypes(newCartTypes);
  };

  const handleWaitingTimeSpecificCartsChange = (newCarts) => {
    setSelectedWaitingTimeCarts(newCarts);
  };

  // Trip filter handlers
  const handleTripCartTypesChange = (newCartTypes) => {
    // Prevent deselecting all cart types - minimum 1 must be selected
    if (newCartTypes.length === 0) return;
    setSelectedTripCartTypes(newCartTypes);
  };

  const handleTripSpecificCartsChange = (newCarts) => {
    setSelectedTripCarts(newCarts);
  };

  const handleTripStatusesChange = (newStatuses) => {
    // Prevent deselecting all trip statuses - minimum 1 must be selected
    if (newStatuses.length === 0) return;
    setSelectedTripStatuses(newStatuses);
  };

  const handleTripTypeChange = (newTripType) => {
    setTripType(newTripType);
  };

  // Get all filter states as an object for easy passing
  const getFilterStates = () => ({
    // Demand filters
    demandFilterType,
    selectedUserTypes,
    userCreationPeriod,
    selectedUsers,
    demandYAxisPercentage,
    
    // Availability filters
    availabilityCartFilterType,
    selectedAvailabilityCartTypes,
    selectedAvailabilityCarts,
    availabilityYAxisPercentage,
    
    // OnService filters
    onServiceCartFilterType,
    selectedOnServiceCartTypes,
    selectedOnServiceCarts,
    onServiceYAxisPercentage,
    
    // WaitingTime filters
    waitingTimeCartFilterType,
    selectedWaitingTimeCartTypes,
    selectedWaitingTimeCarts,
    waitingTimeYAxisPercentage,
    
    // Trip filters
    tripCartFilterType,
    selectedTripCartTypes,
    selectedTripCarts,
    selectedTripStatuses,
    tripType,
    tripYAxisPercentage
  });

  return {
    // Filter states
    demandFilterType,
    setDemandFilterType,
    selectedUserTypes,
    userCreationPeriod,
    selectedUsers,
    demandYAxisPercentage,
    
    availabilityCartFilterType,
    setAvailabilityCartFilterType,
    selectedAvailabilityCartTypes,
    selectedAvailabilityCarts,
    availabilityYAxisPercentage,
    
    onServiceCartFilterType,
    setOnServiceCartFilterType,
    selectedOnServiceCartTypes,
    selectedOnServiceCarts,
    onServiceYAxisPercentage,
    
    waitingTimeCartFilterType,
    setWaitingTimeCartFilterType,
    selectedWaitingTimeCartTypes,
    selectedWaitingTimeCarts,
    waitingTimeYAxisPercentage,
    
    tripCartFilterType,
    setTripCartFilterType,
    selectedTripCartTypes,
    selectedTripCarts,
    selectedTripStatuses,
    tripType,
    tripYAxisPercentage,
    
    cartSummaryFilter,
    setCartSummaryFilter,
    
    // Filter handlers
    handleUserTypesChange,
    handleCreationPeriodChange,
    handleSpecificUsersChange,
    handleDemandYAxisPercentageChange,
    
    handleAvailabilityCartTypesChange,
    handleAvailabilitySpecificCartsChange,
    handleAvailabilityYAxisPercentageChange,
    
    handleOnServiceCartTypesChange,
    handleOnServiceSpecificCartsChange,
    handleOnServiceYAxisPercentageChange,
    
    handleWaitingTimeCartTypesChange,
    handleWaitingTimeSpecificCartsChange,
    handleWaitingTimeYAxisPercentageChange,
    
    handleTripCartTypesChange,
    handleTripSpecificCartsChange,
    handleTripStatusesChange,
    handleTripTypeChange,
    handleTripYAxisPercentageChange,
    
    // Utility
    getFilterStates
  };
};

export default useStatisticsFilters;
